import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import './screens.css'

interface ActivityEvent {
  id: string
  workspace_id: string | null
  event_type: string | null
  tool_name: string | null
  server_type: string | null
  status: string | null
  duration_ms: number | null
  created_at: string
}
interface ActivityResponse {
  events: ActivityEvent[]
  limit: number
  offset: number
  has_more: boolean
}

const LIMIT = 50

export function ActivityView() {
  const [workspaceId, setWorkspaceId] = useState('')
  const [status, setStatus] = useState('')
  const [eventType, setEventType] = useState('')
  const [since, setSince] = useState('')
  const [offset, setOffset] = useState(0)

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) })
  if (workspaceId.trim()) params.set('workspace_id', workspaceId.trim())
  if (status) params.set('status', status)
  if (eventType) params.set('event_type', eventType)
  if (since) params.set('since', new Date(since).toISOString())

  const query = useQuery<ActivityResponse>({
    queryKey: ['admin-activity', workspaceId, status, eventType, since, offset],
    queryFn: () => apiFetch<ActivityResponse>(`/admin/activity?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
  useForbiddenGuard(query.error)

  // Any filter change resets paging to the first page.
  function onFilter<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v)
      setOffset(0)
    }
  }

  const events = query.data?.events ?? []
  const hasMore = query.data?.has_more ?? false

  return (
    <section className="scr" aria-labelledby="activity-heading">
      <div className="screen-head">
        <h2 id="activity-heading">Activity Log</h2>
      </div>

      <div className="scr-filters">
        <input
          type="text"
          aria-label="Workspace ID"
          placeholder="Workspace ID"
          value={workspaceId}
          onChange={(e) => onFilter(setWorkspaceId)(e.target.value)}
        />
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => onFilter(setStatus)(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="success">success</option>
          <option value="error">error</option>
          <option value="denied">denied</option>
        </select>
        <select
          aria-label="Event type"
          value={eventType}
          onChange={(e) => onFilter(setEventType)(e.target.value)}
        >
          <option value="">All events</option>
          <option value="tool_call">tool_call</option>
          <option value="governance">governance</option>
        </select>
        <input
          type="datetime-local"
          aria-label="Since"
          value={since}
          onChange={(e) => onFilter(setSince)(e.target.value)}
        />
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load activity.
        </p>
      )}

      <table className="scr-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Workspace</th>
            <th>Event</th>
            <th>Tool</th>
            <th>Target</th>
            <th>Status</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.created_at).toLocaleString()}</td>
              <td>
                <code className="scr-mono">{e.workspace_id?.slice(0, 8) ?? '—'}</code>
              </td>
              <td>{e.event_type ?? '—'}</td>
              <td>{e.tool_name ?? '—'}</td>
              <td>{e.server_type ?? '—'}</td>
              <td>
                <span className={`scr-badge scr-badge--${e.status ?? 'unknown'}`}>
                  {e.status ?? '—'}
                </span>
              </td>
              <td>{e.duration_ms != null ? `${e.duration_ms} ms` : '—'}</td>
            </tr>
          ))}
          {!query.isLoading && events.length === 0 && (
            <tr>
              <td colSpan={7} className="ws-empty">
                No activity.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="ws-pager">
        <button
          type="button"
          disabled={offset <= 0}
          onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
        >
          Previous
        </button>
        <span className="ws-count">
          {events.length === 0 ? '0' : `${offset + 1}–${offset + events.length}`}
        </span>
        <button type="button" disabled={!hasMore} onClick={() => setOffset((o) => o + LIMIT)}>
          Next
        </button>
      </div>
    </section>
  )
}
